import { AdoraHttpClient } from '../utils/http-client';
import { TokenService } from './token-service';
import { Util } from "../utils/util"; 

/**
 * HttpService handles HTTP requests enriched with store-specific headers such as store key and business date.
 * It also manages token injection and header-based token updates using TokenService.
 */
export class HttpService {

    /**
     * Sends an HTTP request with contextual store headers and optional authentication tokens.
     *
     * @param {string} method - The HTTP method (e.g., 'GET', 'POST').
     * @param {string} url - The request URL.
     * @param {object | null} payload - The request body payload (for POST/PUT requests).
     * @param {string} storeKey - The store key.
     * @param {string} businessDate - The store business date.
     * @param {object} [options] - Optional settings for the request.
     * @param {boolean} [options.showProgress=true] - Whether to show progress indication.
     * @param {boolean} [options.noCache=true] - Whether to prevent caching via headers.
     * @returns {Promise<Response>} A Promise resolving to the fetch Response object.
     */
    public async request(
        method: string,
        url: string,
        payload: object | null,
        storeKey: string,
        businessDate: string,
        options?: {
            showProgress?: boolean,
            noCache?: boolean
        }
    ): Promise<Response> {
        const response = await AdoraHttpClient.httpRequest(
            options?.showProgress ?? true,
            method,
            url,
            payload ?? null,
            storeKey,
            businessDate,
            TokenService.getJWT(),
            TokenService.getRefreshToken(),
            options?.noCache ?? true
        );

        TokenService.updateTokensFromHeaders(response);

        if (response.status === 500) {
            // optional: log or handle error here
            await this.getJson(response);
        } else if (response.status === 401) {
            TokenService.removeJWT();
            TokenService.removeRefreshToken();
        }

        return response;
    }

    /**
     * Parses a JSON response if the content type is application/json.
     *
     * @template T
     * @param {Response} response - The fetch response object to parse.
     * @returns {Promise<T | null>} A Promise resolving to the parsed JSON object or null if parsing fails or the content is not JSON.
     */
    public async getJson<T = any>(response: Response, options?: { parseDates?: boolean }): Promise<T | null> {
        try {
            const contentType = response.headers.get("Content-Type") ?? "";

            if (!contentType.includes("application/json")) return null;

            if (options?.parseDates) {
                const text = await response.text();
                return Util.parseJsonWithDates<T>(text);
            }

            return await response.json();
        } catch {
            return null;
        }
    }

    /**
     * Parses a JSON response if the content type is application/json.
     *
     * @template T
     * @param {Response} response - The fetch response object to parse.
     * @returns {Promise<T | null>} A Promise resolving to the parsed JSON object or null if parsing fails or the content is not JSON.
     */
    public async getText(response: Response): Promise<string | null> {
        try {
            const contentType = response.headers.get("Content-Type") ?? "";

            if (!contentType.includes("application/json")) {
                return await response.text();
            }

            return null;
        } catch {
            return null;
        }
    }
}